/**
 * The time axis, shared by everything that draws dates: the state strip, the radar series
 * and the calendar.
 *
 * It exists because the same three rules were being invented separately in each of them,
 * and they disagreed. `collapseByDate` in particular: calendar.ts already said "accepted
 * wins the slot" for two granules of one overpass, lib/strip.ts did not, so the strip drew
 * them on top of each other and its own counters did not add up.
 *
 * Nothing here decides anything about a field — it only says where on a line a date goes.
 */

export interface AxisTick {
  /** 0..1 along the axis. */
  position: number;
  label: string;
  major: boolean;
}

const MONTHS_SHORT = [
  "янв",
  "фев",
  "мар",
  "апр",
  "май",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек",
];

/** One tick per month boundary inside the window. */
export const monthTicks = (startMs: number, endMs: number, spanMs: number): AxisTick[] => {
  const ticks: AxisTick[] = [];
  const cursor = new Date(startMs);
  cursor.setUTCDate(1);
  cursor.setUTCHours(0, 0, 0, 0);
  while (cursor.getTime() <= endMs) {
    if (cursor.getTime() >= startMs) {
      ticks.push({
        position: (cursor.getTime() - startMs) / spanMs,
        label: MONTHS_SHORT[cursor.getUTCMonth()] ?? "",
        major: true,
      });
    }
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return ticks;
};

/**
 * One row per date. Two granules of the same overpass are one acquisition as far as the
 * eye is concerned, and the accepted one is the row that fed the decision — so it takes
 * the slot. Rows without a date are returned untouched, in order: they are a fact of their
 * own ("the pipeline could not date this one") and merging them would erase it.
 */
export const collapseByDate = <T>(
  rows: readonly T[],
  dateOf: (row: T) => string | null,
  acceptedOf: (row: T) => boolean,
): T[] => {
  const byDate = new Map<string, T>();
  const undated: T[] = [];
  for (const row of rows) {
    const date = dateOf(row);
    if (!date) {
      undated.push(row);
      continue;
    }
    const existing = byDate.get(date);
    if (!existing || (!acceptedOf(existing) && acceptedOf(row))) byDate.set(date, row);
  }
  return [...byDate.values(), ...undated];
};

/**
 * The smallest distance between two neighbouring marks, as a fraction of the axis.
 * A column drawn wider than this overlaps its neighbour — over Russia at 55-65° N
 * Sentinel-2 passes on consecutive days routinely, so this is the normal case, not an edge.
 */
export const minGapFraction = (positions: readonly number[]): number => {
  const sorted = [...positions].sort((a, b) => a - b);
  let smallest = 1;
  for (let index = 1; index < sorted.length; index += 1) {
    const gap = (sorted[index] as number) - (sorted[index - 1] as number);
    if (gap > 0 && gap < smallest) smallest = gap;
  }
  return smallest;
};
